// Module 10 (viz) collaborator — the per-edge render-material STATE MACHINE, extracted VERBATIM from the
// adapter's render hot loop (P10 / task #136, the deferred adapter.ts cut). Decides each branch's visual
// state from the engine's BranchFlow + the energy-layer toggle, and owns the SKIP-GUARD so a steady edge
// never re-batches its GPU polyline collection (the documented Cesium FPS-cliff guard, viz.md). Pure
// decision (`stateFor`) + the owned change-detection map + the transformer-leg set (windings render SOLID,
// never marching chevrons). The adapter keeps the GPU material maps and performs the lookup; this owns
// only the LOGIC + change detection — so the commit happens ONLY when the adapter actually found AND set a
// material (byte-faithful to the inline original: a missing material left the state uncommitted ⇒ it was
// retried the next tick). Same-module collaborator: imports nothing engine/Cesium (CLAUDE.md boundary).
export type FlowState = "F" | "R" | "IDLE" | "OFF";

export interface FlowBranch {
  edgeId: unknown; // EdgeId-ish (string-coerced); the adapter passes the snapshot BranchFlow
  flowMw: number;
  energized?: number | boolean; // optional to match BranchFlow; undefined is falsy ⇒ de-energized branch
  inService?: number | boolean;
}

// P14 loading/danger ramp: the committed key folds in the loading BAND (base/warn/hot/red) alongside the
// F/R/IDLE/OFF state, so an edge that crosses a band boundary re-batches to the danger-tinted material but
// a steady-band edge still skips (the FPS-cliff guard holds). The band is irrelevant for a de-energized
// OFF edge (it carries ~0 flow), so OFF normalises to band "base" — a dead edge never re-batches on a
// phantom band flicker. The band param DEFAULTS "base" so a caller that doesn't track loading is unchanged.
export type CommitBand = "base" | "load" | "warn" | "hot" | "red"; // P14c: +load (≥40%) so a working edge re-batches into its warmed-tier material

export class EdgeFlowStateMachine {
  private readonly committed = new Map<string, string>(); // edge id → last APPLIED `${state}:${band}` key
  private readonly legs = new Set<string>(); // transformer-winding edges → SOLID, never marching arrows

  // buildEdges: seed an edge at OFF (its initial static material) + flag transformer windings.
  register(edgeId: string, isTransformerLeg: boolean): void {
    if (isTransformerLeg) this.legs.add(edgeId);
    this.committed.set(edgeId, "OFF:base");
  }

  isTransformerLeg(edgeId: string): boolean {
    return this.legs.has(edgeId);
  }

  // dispose: drop the change-detection state only — transformer legs are static model geometry, re-added
  // by the next mount's buildEdges (matches the inline original, which cleared only edgeFlowState).
  clear(): void {
    this.committed.clear();
  }

  // PURE: the desired render state for a branch given the energy-layer toggle.
  //   OFF  energy layer hidden, OR de-energized & out of service ⇒ faint line.
  //   F/R  energized & flowing ⇒ marching chevrons (R when flowMw < 0), UNLESS a transformer winding.
  //   IDLE energized winding, OR in-service with ~0 flow ⇒ solid tier line, no arrows (reads as live).
  stateFor(bf: FlowBranch, energyVisible: boolean): FlowState {
    if (!energyVisible) return "OFF";
    if (bf.energized) return this.legs.has(String(bf.edgeId)) ? "IDLE" : bf.flowMw < 0 ? "R" : "F";
    return bf.inService ? "IDLE" : "OFF";
  }

  // a committed key normalises the band to "base" when the state is OFF (a dead edge carries no load).
  private keyOf(state: FlowState, band: CommitBand): string {
    return `${state}:${state === "OFF" ? "base" : band}`;
  }

  // render skip-guard: the wanted state IFF its (state,band) differs from the committed one (else null ⇒
  // skip the swap). `band` (P14) folds the loading-danger ramp into the change detection.
  wantedIfChanged(bf: FlowBranch, energyVisible: boolean, band: CommitBand = "base"): FlowState | null {
    const want = this.stateFor(bf, energyVisible);
    return this.committed.get(String(bf.edgeId)) === this.keyOf(want, band) ? null : want;
  }

  // render: record that the adapter applied this (state,band) material (called ONLY when a material was found).
  commit(edgeId: string, state: FlowState, band: CommitBand = "base"): void {
    this.committed.set(edgeId, this.keyOf(state, band));
  }

  // force the next wantedIfChanged to re-report (so the adapter re-applies the engine material). Used by
  // the edge-SELECTION highlight to RESTORE an edge whose polyline material was overridden while selected.
  invalidate(edgeId: string): void {
    this.committed.delete(edgeId);
  }
}
